import { DollarSign, Tag } from 'lucide-react'
import type { SoapCalculations, Unit } from "@/data/oilDatabase"
import {
  barWeightForUnit,
  calculatePricing,
  GRAMS_PER_OUNCE,
  type PricingInputs,
} from "@/utils/pricing"

interface CostBreakdownProps {
  calculations: SoapCalculations
  unit: Unit
  batchSize: number
  hasOils: boolean
  pricing: PricingInputs
  onPricingChange: (pricing: PricingInputs) => void
}

export function CostBreakdown({
  calculations,
  unit,
  batchSize,
  hasOils,
  pricing,
  onPricingChange,
}: CostBreakdownProps) {
  const { oilsWithCalculatedWeights, totalCost, totalBatchWeight } = calculations
  const costPerBatchUnit = batchSize > 0 ? totalCost / batchSize : 0
  const oilsWithCost = oilsWithCalculatedWeights.filter((o) => o.costPerUnit > 0)

  const displayBarWeight = barWeightForUnit(pricing.barWeightGrams, unit)

  const handleBarWeightChange = (raw: string) => {
    const value = Number(raw)
    if (!Number.isFinite(value)) return
    onPricingChange({
      ...pricing,
      barWeightGrams: unit === "grams" ? value : value * GRAMS_PER_OUNCE,
    })
  }

  const pricingResult = calculatePricing(totalCost, totalBatchWeight, unit, pricing)
  const { costPerBar, barsPerBatch, profitPerBar, recommendedPrice, roundedPrice, wholesalePrice } =
    pricingResult
  const targetMargin = pricing.targetMargin
  const extraCostPerBatch = pricing.extraCostPerBatch

  return (
    <div className="bg-white rounded-xl shadow-md p-6">
      <div className="flex items-center gap-2 mb-4">
        <DollarSign size={18} className="text-emerald-600" />
        <h2 className="text-lg font-bold text-gray-900">Cost Breakdown</h2>
      </div>

      {hasOils && oilsWithCost.length > 0 ? (
        <div className="space-y-3">
          <div className="space-y-2">
            {oilsWithCost.map((oil) => {
              const lineCost = oil.weight * oil.costPerUnit
              return (
                <div key={oil.id} className="flex items-center justify-between text-sm">
                  <span className="text-gray-700 truncate mr-2">{oil.oil}</span>
                  <span className="font-medium text-gray-900 whitespace-nowrap">
                    ${lineCost.toFixed(2)}
                  </span>
                </div>
              )
            })}
          </div>

          <div className="border-t border-gray-200 pt-3 space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm font-semibold text-gray-700">Total Oil Cost</span>
              <span className="text-base font-bold text-emerald-600">
                ${totalCost.toFixed(2)}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-xs text-gray-500">Cost per {unit === "grams" ? "gram" : "ounce"} of oils</span>
              <span className="text-xs font-medium text-gray-700">
                ${costPerBatchUnit.toFixed(4)}
              </span>
            </div>
          </div>

          <p className="text-xs text-gray-400 pt-1">
            Excludes lye, liquid, and fragrance costs.
          </p>

          {/* Pricing helper */}
          <div className="border-t border-gray-200 pt-4">
            <div className="flex items-center gap-2 mb-3">
              <Tag size={15} className="text-emerald-600" />
              <h3 className="text-sm font-bold text-gray-900">Retail Pricing Helper</h3>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <label className="block">
                <span className="block text-xs font-medium text-gray-500 mb-1">
                  Bar weight ({unit === "grams" ? "g" : "oz"})
                </span>
                <input
                  type="number"
                  min={1}
                  step={unit === "grams" ? 5 : 0.25}
                  value={displayBarWeight}
                  onChange={(e) => handleBarWeightChange(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                />
              </label>

              <label className="block">
                <span className="block text-xs font-medium text-gray-500 mb-1">Target margin (%)</span>
                <input
                  type="number"
                  min={0}
                  max={95}
                  step={5}
                  value={targetMargin}
                  onChange={(e) => onPricingChange({ ...pricing, targetMargin: Number(e.target.value) })}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                />
              </label>
            </div>

            <label className="block mt-3">
              <span className="block text-xs font-medium text-gray-500 mb-1">
                Extra cost per batch ($)
              </span>
              <input
                type="number"
                min={0}
                step={0.5}
                value={extraCostPerBatch}
                onChange={(e) => onPricingChange({ ...pricing, extraCostPerBatch: Number(e.target.value) })}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              />
              <span className="block text-xs text-gray-400 mt-1">
                Lye, fragrance, packaging, labels.
              </span>
            </label>

            <div className="mt-4 rounded-lg bg-emerald-50 p-3 space-y-2">
              <div className="flex items-center justify-between text-xs">
                <span className="text-gray-600">Bars per batch</span>
                <span className="font-medium text-gray-900">
                  {barsPerBatch > 0 && Number.isFinite(barsPerBatch) ? barsPerBatch.toFixed(1) : "—"}
                </span>
              </div>
              <div className="flex items-center justify-between text-xs">
                <span className="text-gray-600">Cost per bar</span>
                <span className="font-medium text-gray-900">${costPerBar.toFixed(2)}</span>
              </div>
              <div className="flex items-center justify-between text-xs">
                <span className="text-gray-600">Profit per bar</span>
                <span className="font-medium text-gray-900">${profitPerBar.toFixed(2)}</span>
              </div>
              <div className="flex items-center justify-between border-t border-emerald-200 pt-2">
                <span className="text-sm font-semibold text-gray-700">Retail price</span>
                <span className="text-lg font-bold text-emerald-700">
                  ${recommendedPrice.toFixed(2)}
                </span>
              </div>
              <div className="flex items-center justify-between text-xs">
                <span className="text-gray-600">Suggested rounding</span>
                <span className="font-medium text-gray-900">${roundedPrice.toFixed(2)}</span>
              </div>
              <div className="flex items-center justify-between text-xs">
                <span className="text-gray-600">Wholesale (50%)</span>
                <span className="font-medium text-gray-900">${wholesalePrice.toFixed(2)}</span>
              </div>
            </div>

            <p className="text-xs text-gray-400 mt-2">
              Retail = cost per bar ÷ (1 − margin). Raise the extra cost to include packaging.
            </p>
          </div>
        </div>
      ) : (
        <div className="text-center py-8 text-gray-500">
          <p className="text-sm">
            {hasOils ? "Add a cost per unit to your oils to see cost breakdown" : "Add oils to see cost breakdown"}
          </p>
        </div>
      )}
    </div>
  )
}
